import React, { useEffect, useRef, useState } from 'react';

const REVEAL_TIMEOUT_MS = 1200;

function canAnimate() {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * KDS reveal-on-scroll. Content is visible by default; hiding is opted into
 * only when IntersectionObserver exists and reduced motion is not requested,
 * so text is never trapped behind an effect that cannot run.
 */
export default function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [pending, setPending] = useState(canAnimate);

  useEffect(() => {
    if (!pending || !ref.current) return undefined;
    const node = ref.current;
    const reveal = () => setPending(false);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reveal();
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(node);
    const timer = window.setTimeout(reveal, REVEAL_TIMEOUT_MS);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [pending]);

  const style = delay ? { transitionDelay: `${delay}ms` } : undefined;
  return (
    <Tag ref={ref} className={`kds-reveal${pending ? ' kds-reveal--pending' : ''} ${className}`.trim()} style={style} {...rest}>
      {children}
    </Tag>
  );
}
